import React from "react";
import { useTranslation } from "react-i18next";
import { Award, Heart, Shield, Star, Map } from "lucide-react";

import { Card, CardContent } from "../ui/card";
import florianopolisImg from "../../assets/images/florianopolis.webp";

const features = [
  { key: "local", icon: Award },
  { key: "safety", icon: Shield },
  { key: "care", icon: Heart },
  { key: "rating", icon: Star },
] as const;

export function About() {
  const { t } = useTranslation();

  return (
    <section id="about" className="bg-white py-16 sm:py-24">
      <div className="container mx-auto px-4">
        <div className="mb-10 grid grid-cols-1 items-center gap-8 sm:mb-16 lg:grid-cols-2 lg:gap-12">
          <div>
            <h2 className="mb-6 text-3xl font-bold text-slate-900 sm:text-4xl md:text-5xl">
              {t("about.title")} <span className="text-primary">{t("about.highlight")}</span>
            </h2>
            <p className="mb-6 text-lg leading-relaxed text-slate-600">{t("about.p1")}</p>
            <p className="text-lg leading-relaxed text-slate-600">{t("about.p2")}</p>
          </div>
          <div className="group relative h-72 overflow-hidden rounded-2xl shadow-2xl sm:h-96">
            <img
              src={florianopolisImg}
              alt={t("about.imageAlt")}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/60 to-transparent p-6 sm:p-8">
              <div>
                <Map className="mb-2 h-10 w-10 text-primary" />
                <p className="text-2xl font-bold text-white">{t("about.imageCaption")}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
          {features.map((feature) => (
            <Card
              key={feature.key}
              className="border-slate-100 bg-slate-50/50 text-center transition-all hover:-translate-y-1 hover:shadow-lg"
            >
              <CardContent className="pt-6">
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <feature.icon className="h-8 w-8" />
                </div>
                <h3 className="mb-2 text-xl font-bold text-slate-900">
                  {t(`about.features.${feature.key}.title`)}
                </h3>
                <p className="text-sm leading-relaxed text-slate-600">
                  {t(`about.features.${feature.key}.description`)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
